/* ==========================================================================
   Pergola Cave 2026 — theme script (vanilla, no dependencies)
   Modules: attribution, header, mobile menu, carousel, cart drawer, product, topic fields.
   ========================================================================== */
(function () {
  'use strict';

  var PC = window.PC = window.PC || {};
  var $html = document.documentElement;

  /* ---------- Helpers ---------- */
  function money(cents) {
    try {
      return new Intl.NumberFormat(PC.locale || 'en-US', { style: 'currency', currency: PC.currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function lockScroll(on) {
    $html.classList.toggle('is-locked', on);
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var err = new Error(data.description || data.message || 'Request failed');
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function sizeNumbers(raw) {
    var m = String(raw || '').match(/\d+(?:\.\d+)?/g);
    return m ? m.map(Number) : [];
  }

  function isSizeName(name) {
    return /size|length/i.test(name || '');
  }

  function isColorName(name) {
    return /colou?r/i.test(name || '');
  }

  /* ---------- Attribution (first touch) ---------- */
  var ATTR_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid', 'fbclid'];
  var ATTR_STORE = 'pc_attr_v1';

  function initAttribution() {
    var stored = null;
    try { stored = JSON.parse(window.localStorage.getItem(ATTR_STORE) || 'null'); } catch (e) { stored = null; }

    var params = new URLSearchParams(window.location.search);
    var hasParams = ATTR_KEYS.some(function (k) { return params.get(k); });

    if (!stored || hasParams) {
      stored = { landing: window.location.href.split('#')[0], referrer: document.referrer || '' };
      ATTR_KEYS.forEach(function (k) { if (params.get(k)) { stored[k] = params.get(k).slice(0, 200); } });
      try { window.localStorage.setItem(ATTR_STORE, JSON.stringify(stored)); } catch (e) { /* storage blocked */ }
    }

    var $fields = document.querySelectorAll('[data-attr]');
    Array.prototype.forEach.call($fields, function (field) {
      var key = field.getAttribute('data-attr');
      if (stored[key]) { field.value = stored[key]; }
    });

    /* Several contact forms can render a success state after one post; prefer the one Shopify anchored to (#form-id). */
    var $posted = window.location.hash.length > 1 ? document.getElementById(window.location.hash.slice(1)) : null;
    var $success = ($posted && $posted.querySelector('[data-quote-success]')) || document.querySelector('[data-quote-success]');
    if ($success) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'generate_lead', form_name: $success.getAttribute('data-form-name') || 'quote', page_path: window.location.pathname });
      $success.scrollIntoView({ block: 'center' });
      $success.focus({ preventScroll: true });
    }
  }

  /* ---------- Header dropdowns ---------- */
  function initHeader() {
    var $drops = document.querySelectorAll('[data-drop]');

    function setOpen(item, open) {
      item.classList.toggle('is-open', open);
      var trigger = item.querySelector('[data-drop-trigger]');
      if (trigger) { trigger.setAttribute('aria-expanded', open ? 'true' : 'false'); }
    }

    Array.prototype.forEach.call($drops, function (item) {
      item.addEventListener('mouseenter', function () { setOpen(item, true); });
      item.addEventListener('mouseleave', function () { setOpen(item, false); });
      item.addEventListener('focusin', function () { setOpen(item, true); });
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) { setOpen(item, false); }
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') { return; }
      Array.prototype.forEach.call($drops, function (item) { setOpen(item, false); });
    });
  }

  /* ---------- Mobile menu ---------- */
  function initMobileMenu() {
    var $menu = document.querySelector('[data-menu]');
    var $openBtn = document.querySelector('[data-menu-open]');
    if (!$menu || !$openBtn) { return; }

    function toggle(open) {
      $menu.hidden = !open;
      $openBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      lockScroll(open);
      if (open) {
        var first = $menu.querySelector('button, a');
        if (first) { first.focus(); }
      } else {
        $openBtn.focus();
      }
    }

    $openBtn.addEventListener('click', function () { toggle(true); });
    $menu.addEventListener('click', function (e) {
      if (e.target.closest('[data-menu-close]')) { toggle(false); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !$menu.hidden) { toggle(false); }
    });
  }

  /* ---------- Carousel ---------- */
  function initCarousels() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-carousel-prev], [data-carousel-next]');
      if (!btn) { return; }
      var root = btn.closest('[data-carousel]');
      var track = root && root.querySelector('[data-carousel-track]');
      if (!track) { return; }
      var card = track.querySelector('[data-card]');
      var amount = card ? card.offsetWidth + 20 : 320;
      var dir = btn.hasAttribute('data-carousel-prev') ? -1 : 1;
      track.scrollBy({ left: dir * amount, behavior: 'smooth' });
    });
  }

  /* ---------- Cart drawer ---------- */
  var Cart = {
    $drawer: null,
    $lastFocus: null,

    init: function () {
      this.$drawer = document.querySelector('[data-cart-drawer]');
      if (!this.$drawer) { return; }
      var self = this;

      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-cart-open]')) {
          e.preventDefault();
          self.open();
          return;
        }
        if (e.target.closest('[data-cart-close]')) {
          self.close();
          return;
        }
        var qtyBtn = e.target.closest('[data-qty]');
        if (qtyBtn && self.$drawer.contains(qtyBtn)) {
          var line = qtyBtn.closest('[data-line]');
          if (line) { self.change(Number(line.getAttribute('data-line')), Number(qtyBtn.getAttribute('data-qty'))); }
        }
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !self.$drawer.hidden) { self.close(); }
      });
    },

    open: function () {
      this.$lastFocus = document.activeElement;
      this.$drawer.hidden = false;
      lockScroll(true);
      var closeBtn = this.$drawer.querySelector('.drawer__head [data-cart-close]');
      if (closeBtn) { closeBtn.focus(); }
    },

    close: function () {
      this.$drawer.hidden = true;
      lockScroll(false);
      if (this.$lastFocus && this.$lastFocus.focus) { this.$lastFocus.focus(); }
    },

    render: function (sections) {
      var html = sections && sections['cart-drawer'];
      if (!html) { return; }
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-cart-panel]');
      var current = this.$drawer.querySelector('[data-cart-panel]');
      if (fresh && current) { current.innerHTML = fresh.innerHTML; }
      var body = doc.querySelector('[data-cart-body]');
      this.setCount(body ? Number(body.getAttribute('data-count')) : 0);
    },

    setCount: function (n) {
      var $counts = document.querySelectorAll('[data-cart-count]');
      Array.prototype.forEach.call($counts, function (el) {
        el.textContent = n;
        el.hidden = n === 0;
      });
    },

    change: function (line, quantity) {
      var self = this;
      self.$drawer.classList.add('is-busy');
      postJSON(PC.routes.cart_change, { line: line, quantity: Math.max(0, quantity), sections: 'cart-drawer', sections_url: window.location.pathname })
        .then(function (data) { self.render(data.sections); })
        .catch(function () { window.location.href = PC.routes.cart; })
        .then(function () { self.$drawer.classList.remove('is-busy'); });
    }
  };

  /* Public cart API for other scripts (e.g. the 3D builder module):
     PC.cart.addItems([{ id, quantity, properties }]) -> Promise; re-renders + opens the drawer. */
  PC.cart = {
    addItems: function (items) {
      return postJSON(PC.routes.cart_add, { items: items, sections: 'cart-drawer', sections_url: window.location.pathname })
        .then(function (data) {
          Cart.render(data.sections);
          Cart.open();
          return data;
        });
    },
    open: function () { Cart.open(); }
  };

  /* ---------- Product ---------- */
  function Product(root) {
    this.$root = root;
    var jsonEl = root.querySelector('[data-product-json]');
    if (!jsonEl) { return; }
    this.data = JSON.parse(jsonEl.textContent);
    this.$form = root.querySelector('[data-product-form]');
    this.$variantInput = root.querySelector('[data-variant-id]');
    this.$options = root.querySelectorAll('[data-option]');
    this.$price = root.querySelector('[data-price]');
    this.$compare = root.querySelector('[data-compare]');
    this.$save = root.querySelector('[data-save]');
    this.$saveAmount = root.querySelector('[data-save-amount]');
    this.$ready = root.querySelector('[data-ready-banner]');
    this.$total = root.querySelector('[data-total]');
    this.$addonNote = root.querySelector('[data-addon-note]');
    this.$add = root.querySelector('[data-add]');
    this.$error = root.querySelector('[data-add-error]');
    this.$mainImg = root.querySelector('[data-gallery-main]');
    this.$quoteLink = root.querySelector('[data-quote-link]');
    this.unit = 'ft';
    this.variant = null;
    this.accState = {};
    this.bind();
    this.initUnits();
    this.initAccessories();
    this.update(false);
  }

  Product.prototype.selected = function () {
    var values = [];
    Array.prototype.forEach.call(this.$options, function (fs) {
      var checked = fs.querySelector('input:checked');
      values[Number(fs.getAttribute('data-position')) - 1] = checked ? checked.value : null;
    });
    return values;
  };

  Product.prototype.findVariant = function (values) {
    var variants = this.data.variants;
    if (!this.$options.length) { return variants[0] || null; }
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var match = v.options.every(function (o, idx) { return values[idx] === undefined || values[idx] === null || values[idx] === o; });
      if (match) { return v; }
    }
    return null;
  };

  Product.prototype.bind = function () {
    var self = this;

    this.$root.addEventListener('change', function (e) {
      if (e.target.closest('[data-option]')) { self.update(true); }
      var sel = e.target.closest('[data-addon-select]');
      if (sel) {
        var card = sel.closest('[data-addon]');
        var st = self.accState[card.getAttribute('data-addon')];
        st.variantId = Number(sel.value);
        st.touched = true;
        self.renderAccessory(card);
        self.renderTotal();
      }
    });

    this.$root.addEventListener('click', function (e) {
      var thumb = e.target.closest('.pdp-gallery__thumb');
      if (thumb) { self.showMedia(thumb); return; }

      var unitBtn = e.target.closest('[data-unit]');
      if (unitBtn) { self.setUnit(unitBtn.getAttribute('data-unit')); return; }

      var toggle = e.target.closest('[data-addon-toggle]');
      if (toggle) {
        var card = toggle.closest('[data-addon]');
        var st = self.accState[card.getAttribute('data-addon')];
        st.added = !st.added;
        self.renderAccessory(card);
        self.renderTotal();
        return;
      }

      if (e.target.closest('[data-quote-link]')) { self.prefillQuote(); }
    });

    if (this.$form) {
      this.$form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.addToCart();
      });
    }
  };

  Product.prototype.update = function (fromUser) {
    var values = this.selected();
    var variant = this.findVariant(values);
    this.variant = variant;

    // Mark option values that lead to no / unavailable variant given the other selections.
    var self = this;
    Array.prototype.forEach.call(this.$options, function (fs) {
      var pos = Number(fs.getAttribute('data-position')) - 1;
      var legendValue = fs.querySelector('[data-option-value]');
      Array.prototype.forEach.call(fs.querySelectorAll('input'), function (input) {
        var trial = values.slice();
        trial[pos] = input.value;
        var v = self.findVariant(trial);
        input.classList.toggle('is-unavailable', !v || !v.available);
        if (input.checked && legendValue) {
          legendValue.textContent = fs.getAttribute('data-kind') === 'size' ? self.sizeText(input.value) : input.value;
        }
      });
    });

    if (!variant) {
      this.setButton(false, this.data.messages.unavailable);
      if (this.$ready) { this.$ready.hidden = true; }
      return;
    }

    if (this.$variantInput) { this.$variantInput.value = variant.id; }
    this.$price.textContent = money(variant.price);
    var onSale = variant.compare_at_price > variant.price;
    this.$compare.hidden = !onSale;
    this.$save.hidden = !onSale;
    if (onSale) {
      this.$compare.textContent = money(variant.compare_at_price);
      this.$saveAmount.textContent = money(variant.compare_at_price - variant.price);
    }

    if (this.$ready) { this.$ready.hidden = variant.state !== 'in_stock'; }

    if (variant.available) {
      this.setButton(true, this.data.messages.add);
    } else {
      this.setButton(false, this.data.messages.sold_out_btn);
    }

    if (variant.media_id) {
      var thumb = this.$root.querySelector('[data-media-id="' + variant.media_id + '"]');
      if (thumb) { this.showMedia(thumb); }
    }

    if (fromUser) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    this.matchAccessories();
    this.renderTotal();
  };

  Product.prototype.setButton = function (enabled, label) {
    if (!this.$add) { return; }
    this.$add.disabled = !enabled;
    this.$add.textContent = label;
  };

  Product.prototype.showMedia = function (thumb) {
    if (!this.$mainImg) { return; }
    this.$mainImg.srcset = thumb.getAttribute('data-srcset');
    this.$mainImg.src = thumb.getAttribute('data-src');
    this.$mainImg.alt = thumb.getAttribute('data-alt') || '';
    Array.prototype.forEach.call(this.$root.querySelectorAll('.pdp-gallery__thumb'), function (t) {
      t.classList.toggle('is-active', t === thumb);
    });
  };

  /* Size units (ft / m) — only offered when every size value parses to two numbers. */
  Product.prototype.initUnits = function () {
    var self = this;
    Array.prototype.forEach.call(this.$root.querySelectorAll('[data-kind="size"]'), function (fs) {
      var labels = fs.querySelectorAll('[data-size-raw]');
      var ok = labels.length > 0 && Array.prototype.every.call(labels, function (l) { return sizeNumbers(l.getAttribute('data-size-raw')).length >= 2; });
      var units = fs.querySelector('[data-units]');
      if (ok && units) { units.hidden = false; }
    });
    this.sizeText = function (raw) {
      var n = sizeNumbers(raw);
      if (n.length < 2) { return raw; }
      if (self.unit === 'm') { return (n[0] * 0.3048).toFixed(1) + '×' + (n[1] * 0.3048).toFixed(1) + ' m'; }
      return n[0] + '×' + n[1] + ' ft';
    };
  };

  Product.prototype.setUnit = function (unit) {
    var self = this;
    this.unit = unit;
    Array.prototype.forEach.call(this.$root.querySelectorAll('[data-unit]'), function (b) {
      var on = b.getAttribute('data-unit') === unit;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    Array.prototype.forEach.call(this.$root.querySelectorAll('[data-size-raw]'), function (l) {
      l.textContent = self.sizeText(l.getAttribute('data-size-raw')).replace(/ (ft|m)$/, '');
    });
    this.update(false);
  };

  /* Accessories — pick the variant that best matches the chosen pergola size / color. */
  Product.prototype.initAccessories = function () {
    var self = this;
    (this.data.accessories || []).forEach(function (acc) {
      var card = self.$root.querySelector('[data-addon="' + acc.product_id + '"]');
      if (!card) { return; }
      var available = acc.variants.filter(function (v) { return v.available; });
      self.accState[acc.product_id] = { acc: acc, variants: available, variantId: available.length ? available[0].id : null, added: false, touched: false };
      if (available.length > 1) {
        var holder = card.querySelector('[data-addon-variant]');
        var select = document.createElement('select');
        select.setAttribute('data-addon-select', '');
        select.setAttribute('aria-label', 'Option for ' + acc.title);
        available.forEach(function (v) {
          var o = document.createElement('option');
          o.value = v.id;
          o.textContent = v.title;
          select.appendChild(o);
        });
        holder.appendChild(select);
      }
    });
  };

  Product.prototype.matchAccessories = function () {
    var self = this;
    var names = this.data.option_names || [];
    var values = this.variant ? this.variant.options : [];
    var dims = [];
    var color = null;
    names.forEach(function (n, i) {
      if (isSizeName(n)) { dims = sizeNumbers(values[i]); }
      if (isColorName(n)) { color = String(values[i] || '').toLowerCase(); }
    });

    Object.keys(this.accState).forEach(function (id) {
      var st = self.accState[id];
      if (!st.touched && st.variants.length) {
        var best = st.variants[0];
        var bestScore = -Infinity;
        st.variants.forEach(function (v) {
          var score = 0;
          st.acc.option_names.forEach(function (n, i) {
            var val = v.options[i];
            if (isSizeName(n) && dims.length >= 2) {
              var d = sizeNumbers(val);
              if (d.length >= 2) {
                var hit = (Math.abs(d[0] - dims[0]) <= 0.5 && Math.abs(d[1] - dims[1]) <= 0.5) || (Math.abs(d[0] - dims[1]) <= 0.5 && Math.abs(d[1] - dims[0]) <= 0.5);
                score += hit ? 2 : -1;
              }
            }
            if (isColorName(n) && color && String(val).toLowerCase() === color) { score += 1; }
          });
          if (score > bestScore) { bestScore = score; best = v; }
        });
        st.variantId = best.id;
      }
      var card = self.$root.querySelector('[data-addon="' + id + '"]');
      if (card) { self.renderAccessory(card); }
    });
  };

  Product.prototype.accVariant = function (st) {
    for (var i = 0; i < st.variants.length; i++) {
      if (st.variants[i].id === st.variantId) { return st.variants[i]; }
    }
    return null;
  };

  Product.prototype.renderAccessory = function (card) {
    var st = this.accState[card.getAttribute('data-addon')];
    var v = this.accVariant(st);
    var select = card.querySelector('[data-addon-select]');
    if (select && v) { select.value = String(v.id); }
    var price = card.querySelector('[data-addon-price]');
    if (price && v) { price.textContent = money(v.price); }
    var btn = card.querySelector('[data-addon-toggle]');
    card.classList.toggle('is-added', st.added);
    btn.setAttribute('aria-pressed', st.added ? 'true' : 'false');
    btn.textContent = st.added ? 'Added ✓' : 'Add';
  };

  Product.prototype.addedAccessories = function () {
    var self = this;
    return Object.keys(this.accState).map(function (id) { return self.accState[id]; }).filter(function (st) { return st.added && st.variantId; });
  };

  Product.prototype.renderTotal = function () {
    if (!this.$total) { return; }
    var self = this;
    var added = this.addedAccessories();
    var total = this.variant ? this.variant.price : 0;
    added.forEach(function (st) {
      var v = self.accVariant(st);
      if (v) { total += v.price; }
    });
    this.$total.textContent = money(total);
    if (this.$addonNote) {
      this.$addonNote.textContent = added.length ? ' (incl. ' + added.length + ' accessor' + (added.length > 1 ? 'ies' : 'y') + ')' : '';
    }
  };

  Product.prototype.addToCart = function () {
    var self = this;
    if (!this.variant || !this.variant.available) { return; }
    var items = [{ id: this.variant.id, quantity: 1 }];
    this.addedAccessories().forEach(function (st) { items.push({ id: st.variantId, quantity: 1 }); });

    this.$error.hidden = true;
    this.setButton(false, this.data.messages.adding);

    postJSON(PC.routes.cart_add, { items: items, sections: 'cart-drawer', sections_url: window.location.pathname })
      .then(function (data) {
        Cart.render(data.sections);
        Cart.open();
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'add_to_cart_theme', items: items });
      })
      .catch(function (err) {
        self.$error.textContent = err.message;
        self.$error.hidden = false;
      })
      .then(function () {
        self.setButton(true, self.data.messages.add);
      });
  };

  Product.prototype.prefillQuote = function () {
    if (!this.variant) { return; }
    var self = this;
    var parts = [this.variant.title];
    this.addedAccessories().forEach(function (st) {
      var v = self.accVariant(st);
      parts.push('+ ' + st.acc.title + (v && v.title !== 'Default Title' ? ' (' + v.title + ')' : ''));
    });
    var summary = parts.join(' ');
    var hidden = document.querySelector('[data-quote-variant]');
    if (hidden) { hidden.value = summary; }
    var form = document.querySelector('[data-quote-form]');
    var body = form && form.querySelector('textarea[name="contact[body]"]');
    if (body && !body.value.trim()) {
      var title = document.querySelector('.buybox__title');
      body.value = 'I\'m interested in the ' + (title ? title.textContent.trim() : 'pergola') + ': ' + summary + '.';
    }
  };

  function initProducts() {
    var $products = document.querySelectorAll('[data-product-section]');
    Array.prototype.forEach.call($products, function (root) { new Product(root); });
  }

  /* ---------- Topic-dependent form fields ---------- */
  /* A [data-topic-select] shows elements whose data-topic-show lists the chosen value. */
  /* Home pergola cards: thumbnail strip swaps the card's main image. */
  function initCardThumbs() {
    document.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-pcard-thumb]');
      if (!thumb) { return; }
      var card = thumb.closest('.pcard');
      var main = card && card.querySelector('[data-pcard-main]');
      if (!main) { return; }
      main.srcset = thumb.getAttribute('data-srcset');
      main.src = thumb.getAttribute('data-src');
      main.alt = thumb.getAttribute('data-alt') || '';
      Array.prototype.forEach.call(card.querySelectorAll('[data-pcard-thumb]'), function (t) {
        t.classList.toggle('is-active', t === thumb);
      });
    });
  }

  function initTopicFields() {
    var $selects = document.querySelectorAll('[data-topic-select]');
    Array.prototype.forEach.call($selects, function (select) {
      var form = select.form || document;
      var $targets = form.querySelectorAll('[data-topic-show]');
      function sync() {
        Array.prototype.forEach.call($targets, function (el) {
          var show = el.getAttribute('data-topic-show').split(',').indexOf(select.value) !== -1;
          el.hidden = !show;
          Array.prototype.forEach.call(el.querySelectorAll('input, select, textarea'), function (f) { f.disabled = !show; });
        });
      }
      select.addEventListener('change', sync);
      sync();
    });
  }

  /* ---------- Boot ---------- */
  function boot() {
    initAttribution();
    initHeader();
    initMobileMenu();
    initCarousels();
    Cart.init();
    initProducts();
    initCardThumbs();
    initTopicFields();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
